import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Form, Modal, Alert, Row, Col } from "react-bootstrap";
import {
  createSubject,
  updateSubject,
  clearError,
} from "../../redux/slices/subjectSlice";
import collegeService from "../../services/collegeService";
import { toast } from "react-toastify";

const CollegeSubjectModal = ({ show, onHide, subject = null }) => {
  const dispatch = useDispatch();
  const isEditMode = !!subject;

  const { isCreating, isUpdating, error } = useSelector((state) => state.subjects);

  const [formData, setFormData] = useState({
    university_id: "",
    course_id: "",
    semester_id: "",
    subject_name: "",
  });

  const [universities, setUniversities] = useState([]);
  const [courses, setCourses] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  const [isLoadingAcademic, setIsLoadingAcademic] = useState(false);

  // Load Universities
  useEffect(() => {
    if (show) {
      const loadUniversities = async () => {
        try {
          const univData = await collegeService.getUniversities();
          setUniversities(univData);
        } catch (error) {
          console.error("Error loading universities:", error);
        }
      };
      loadUniversities();
    }
  }, [show]);

  // Load Initial Data for Edit Mode
  useEffect(() => {
    if (show && isEditMode && subject) {
      const initEditData = async () => {
        setIsLoadingAcademic(true);
        try {
          // If the subject has a semester_id, we need to find the university and course
          // Assuming the subject row returned from backend has these joined fields
          setFormData({
            university_id: subject.university_id || "",
            course_id: subject.course_id || "",
            semester_id: subject.semester_id || "",
            subject_name: subject.subject_name || "",
          });

          if (subject.university_id) {
            const courseData = await collegeService.getCourses(subject.university_id);
            setCourses(courseData);
          }
          if (subject.course_id) {
            const semData = await collegeService.getSemesters(subject.course_id);
            setSemesters(semData);
          }
        } catch (error) {
          console.error("Error loading academic data for edit:", error);
        } finally {
          setIsLoadingAcademic(false);
        }
      };
      initEditData();
    } else if (show && !isEditMode) {
      setFormData({
        university_id: "",
        course_id: "",
        semester_id: "",
        subject_name: "",
      });
      setCourses([]);
      setSemesters([]);
      setValidationErrors({});
    }
  }, [show, isEditMode, subject]);

  const handleUniversityChange = async (univId) => {
    setFormData(prev => ({ ...prev, university_id: univId, course_id: "", semester_id: "" }));
    setCourses([]);
    setSemesters([]);
    if (univId) {
      try {
        const courseData = await collegeService.getCourses(univId);
        setCourses(courseData);
      } catch (error) {
        console.error("Error loading courses:", error);
      }
    }
  };

  const handleCourseChange = async (courseId) => {
    setFormData(prev => ({ ...prev, course_id: courseId, semester_id: "" }));
    setSemesters([]);
    if (courseId) {
      try {
        const semData = await collegeService.getSemesters(courseId);
        setSemesters(semData);
      } catch (error) {
        console.error("Error loading semesters:", error);
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let finalValue = value;
    if (name === "subject_name") {
      finalValue = value.replace(/[^a-zA-Z\s]/g, "");
    }
    setFormData((prev) => ({ ...prev, [name]: finalValue }));
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.university_id) errors.university_id = "Please select a university";
    if (!formData.course_id) errors.course_id = "Please select a course";
    if (!formData.semester_id) errors.semester_id = "Please select a semester";
    
    const subjectName = formData.subject_name.trim();
    if (!subjectName) {
      errors.subject_name = "Subject name is required";
    } else if (!/^[a-zA-Z\s]+$/.test(subjectName)) {
      errors.subject_name = "Subject name should only contain letters and spaces";
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const submitData = {
      semester_id: formData.semester_id,
      subject_name: formData.subject_name.trim(),
    };

    try {
      if (isEditMode) {
        await dispatch(updateSubject({ id: subject.id, subjectData: submitData })).unwrap();
      } else {
        await dispatch(createSubject(submitData)).unwrap();
      }
      onHide();
    } catch (err) {
      console.error("College Subject operation failed:", err);
    }
  };

  const isSubmitting = isCreating || isUpdating || isLoadingAcademic;

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton className="bg-light">
          <Modal.Title>
            {isEditMode ? "Update College Subject" : "Create New College Subject"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && (
            <Alert variant="danger" dismissible onClose={() => dispatch(clearError())}>
              {error}
            </Alert>
          )}

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-bold text-muted small">UNIVERSITY <span className="text-danger">*</span></Form.Label>
                <Form.Select
                  name="university_id"
                  value={formData.university_id}
                  onChange={(e) => handleUniversityChange(e.target.value)}
                  isInvalid={!!validationErrors.university_id}
                  disabled={isSubmitting}
                >
                  <option value="">Select University</option>
                  {universities.map((univ) => (
                    <option key={univ.id} value={univ.id}>{univ.university_name}</option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">{validationErrors.university_id}</Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-bold text-muted small">COURSE <span className="text-danger">*</span></Form.Label>
                <Form.Select
                  name="course_id"
                  value={formData.course_id}
                  onChange={(e) => handleCourseChange(e.target.value)}
                  isInvalid={!!validationErrors.course_id}
                  disabled={!formData.university_id || isSubmitting}
                >
                  <option value="">Select Course</option>
                  {courses.map((course) => (
                    <option key={course.id} value={course.id}>{course.course_name}</option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">{validationErrors.course_id}</Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-bold text-muted small">SEMESTER <span className="text-danger">*</span></Form.Label>
                <Form.Select
                  name="semester_id"
                  value={formData.semester_id}
                  onChange={handleInputChange}
                  isInvalid={!!validationErrors.semester_id}
                  disabled={!formData.course_id || isSubmitting}
                >
                  <option value="">Select Semester</option>
                  {semesters.map((sem) => (
                    <option key={sem.id} value={sem.id}>{sem.semester_name}</option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">{validationErrors.semester_id}</Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-bold text-muted small">SUBJECT NAME <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="text"
                  name="subject_name"
                  placeholder="Enter subject name"
                  value={formData.subject_name}
                  onChange={handleInputChange}
                  isInvalid={!!validationErrors.subject_name}
                  disabled={isSubmitting}
                />
                <Form.Control.Feedback type="invalid">{validationErrors.subject_name}</Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer className="bg-light">
          <Button variant="secondary" onClick={onHide} disabled={isSubmitting}>Cancel</Button>
          <Button variant="primary" type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Processing..." : isEditMode ? "Update Subject" : "Create Subject"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default CollegeSubjectModal;
